import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowDownToLine, ArrowUpFromLine, CheckCircle2, Gauge, Smartphone, Timer, X, XCircle } from 'lucide-react';
import {
  SPEED_TEST_PATH_LABELS,
  describeSpeedTestPath,
  formatBitRate,
  type LineTestPathChoice,
  type SpeedTest,
  type SpeedTestHistory,
  type StartLineTestResult,
} from '@simpleacs/shared';
import { api } from '../../lib/api';
import { useTasks } from '../../lib/tasks';
import { formatRelative } from '../../lib/format';
import { effectiveLineTestPath, lineTestPathChooserTitle, lineTestPathChoices, requestedLineTestPath } from '../../lib/line-test-path';
import { describeLineTestOutcome, lineTestConfirmation } from '../../lib/line-test-confirmation';
import { Button, Card, Pill } from '../../components/ui';

/**
 * The line test, on the ONU page: the subscriber's CPE measuring its own line
 * over TR-143 (download, upload, then a ping to the platform), started here
 * as a tray job and polled through `GET /onus/:id/speedtest/:runId` until it
 * settles. Two columns (operator, 2026-09-13): on the LEFT the last result as
 * three figures stacked — down, up, latency — with the PATH the platform says
 * it measured under them (the subscriber WAN on a routed unit, the management
 * tunnel on a bridged one — a tunnel figure passed off as a WAN one is the
 * one mistake this card must not make); on the RIGHT the past results as a
 * SCROLLING list (no graph — operator, 2026-09-13), phone results included: a
 * technician's handset files against the same ONU, and "what the phone saw
 * at the wall" beside "what the CPE saw on the line" is the whole diagnosis.
 *
 * The card sits beside the Traffic card at one height (`MatchedPair`), and
 * that height is the TRAFFIC card's: the history list is `contain: size`, so
 * it asks for no height of its own and takes whatever the row leaves it,
 * scrolling inside — before this, twenty-five rows would have stretched the
 * pair and left the traffic graph floating over a void. Stacked on a phone
 * (below `sm`) the column is one fixed height instead, because a list that
 * asks for nothing in a stack gets nothing.
 *
 * Same conventions as the traffic card beside it: download in the app's
 * primary, upload in the teal the semantic palette does not own, bits per
 * second in the plan's unit, a dash where nothing was measured.
 *
 * The PATH CHOOSER (operator, 2026-09-13: "right before the run button add a
 * drop down to choose wan or mngt to test on, default is wan for routed
 * devices, mngt only for bridged devices") is a small native select in the
 * header, before Run. What it offers comes from the PLAN — a dry run posted
 * on mount, `plan.pathOptions` / `plan.defaultPath` — so the card never
 * offers a run the API would refuse: a routed unit gets WAN and Management
 * (Management only where its CPE names a management connection to pin the
 * run to) with WAN selected, a bridged unit (a hotspot, recorded bridged,
 * or a CPE whose subscriber side is bridged) gets Management alone, and a
 * unit the platform cannot judge starts on a "Not known" placeholder with
 * nothing selected. Run posts `{ path }` ONLY when the operator actively
 * picked one (`requestedLineTestPath`) and `{}` otherwise, so the
 * platform's own verdict stands on an untouched chooser — before this the
 * shown default went out on every click, and an undecidable unit's run
 * was filed as the subscriber's WAN and marked as the operator's choice
 * (review, 2026-09-13); the platform pins a Management run to the CPE's
 * management connection over the tunnel. Nothing is persisted: the choice
 * is for this run, and the plan is re-read when a run settles. The phase
 * sentence and the pill keep reading the RUN's path — what was measured,
 * not what was asked.
 */

const DOWN_COLOR = 'var(--primary)';
const UP_COLOR = '#14b8a6';
const POLL_MS = 2_000;
/** Past results fetched for the scrolling column (the API caps at 50). */
const HISTORY_LIMIT = 25;

const settledStatus = (s: SpeedTest['status'] | undefined) =>
  s !== undefined && s !== 'queued' && s !== 'running';

/** Poll one run until it leaves queued/running — what the tray job waits on. */
async function waitForRun(onuId: string, runId: string): Promise<SpeedTest> {
  for (;;) {
    const run = await api.get<SpeedTest>(`/onus/${encodeURIComponent(onuId)}/speedtest/${encodeURIComponent(runId)}`);
    if (settledStatus(run.status)) return run;
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
  }
}

export function LineTestCard({
  onuId,
  serial,
  onNotice,
}: {
  onuId: string;
  serial: string;
  onNotice: (message: string, tone?: 'info' | 'danger') => void;
}) {
  const tasks = useTasks();
  const queryClient = useQueryClient();
  const historyKey = ['speedtest', 'history', onuId];
  const planKey = ['speedtest', 'plan', onuId];
  const [runId, setRunId] = useState<string | null>(null);
  /** Between the click and the platform accepting the run. */
  const [starting, setStarting] = useState(false);
  /** The settled run this card confirms, until dismissed or the next run (2026-10-12). */
  const [outcome, setOutcome] = useState<SpeedTest | null>(null);
  /** The operator's pick on the chooser; null = the plan's default. */
  const [picked, setPicked] = useState<LineTestPathChoice | null>(null);

  // The plan: what this unit can be measured over, and where the chooser
  // starts. A dry run arms nothing. A 400 here (an OMCI unit, a CPE gone
  // quiet) is a verdict, not a blip — not retried; the chooser then stays
  // off and Run posts no path, so the platform decides and answers the same
  // sentence it always did.
  const plan = useQuery({
    queryKey: planKey,
    queryFn: () => api.post<StartLineTestResult>(`/onus/${encodeURIComponent(onuId)}/speedtest`, { dryRun: true }),
    retry: false,
    staleTime: 30_000,
  });
  const choices = lineTestPathChoices(plan.data?.plan);
  /** What the select shows. */
  const path = effectiveLineTestPath(plan.data?.plan, picked);
  /** What Run posts — the operator's active pick alone; null = `{}`, the platform decides. */
  const requested = requestedLineTestPath(plan.data?.plan, picked);

  const history = useQuery({
    queryKey: historyKey,
    // Twenty-five, the most the list can scroll through without a page: the
    // column it sits in is bounded by the Traffic card beside it, so more rows
    // cost no height, only a scroll.
    queryFn: () => api.get<SpeedTestHistory>(`/onus/${encodeURIComponent(onuId)}/speedtests?limit=${HISTORY_LIMIT}`),
  });

  const live = useQuery({
    queryKey: ['speedtest', 'run', onuId, runId],
    enabled: Boolean(runId),
    queryFn: () => api.get<SpeedTest>(`/onus/${encodeURIComponent(onuId)}/speedtest/${encodeURIComponent(runId!)}`),
    refetchInterval: (q) => (settledStatus(q.state.data?.status) ? false : POLL_MS),
  });

  // The card stops following a run once it has settled; the history takes over.
  useEffect(() => {
    if (runId && settledStatus(live.data?.status)) setRunId(null);
  }, [live.data, runId]);

  const items = history.data?.items ?? [];
  /*
    Only a LINE run blocks this card. A phone run sits in the same history
    (a technician's handset, at the wall) and stays queued until it files or
    the reaper closes it; the server does not count it against "one line
    test at a time", and neither may the Run button — nor the phase sentence,
    which speaks of the ONU measuring.
  */
  const inFlight = runId ? live.data : items.find((i) => i.kind === 'line' && !settledStatus(i.status));
  const last = items.find((i) => settledStatus(i.status)) ?? null;
  const running = Boolean(inFlight && inFlight.kind === 'line' && !settledStatus(inFlight.status));

  const start = () => {
    // The operator's ACTIVE pick at the click; `{}` when they made none (or
    // there is no plan yet) — the request the card always made, and the one
    // that keeps the platform's verdict: an untouched chooser is not a
    // choice, and the record must not say it was (review, 2026-09-13).
    const chosen = requested;
    setOutcome(null);
    setStarting(true);
    tasks.start<SpeedTest>({
      label: `Line test ${serial}`,
      run: async () => {
        let started: StartLineTestResult;
        try {
          started = await api.post<StartLineTestResult>(
            `/onus/${encodeURIComponent(onuId)}/speedtest`,
            chosen ? { path: chosen } : {},
          );
        } catch (err) {
          setStarting(false);
          onNotice(`${serial}: the line test did not start — ${err instanceof Error ? err.message : 'the platform refused it'}`, 'danger');
          throw err;
        }
        setStarting(false);
        if (!started.run) throw new Error('The platform answered a plan and no run.');
        setRunId(started.run.id);
        const run = await waitForRun(onuId, started.run.id);
        // The confirmation the operator asked for: said on the card, and a run
        // that did not finish ends RED in the tray, not as a quiet "done".
        setOutcome(run);
        if (run.status !== 'done') throw new Error(`${serial}: ${describeLineTestOutcome(run)}`);
        return run;
      },
      done: (run) => `${serial}: line test finished — ${describeLineTestOutcome(run)}`,
      // The plan too: a tunnel run leaves the CPE's Interface leaf set, and a
      // WAN-mode change between runs moves what the unit can be offered.
      invalidate: [historyKey, planKey],
    });
  };
  const confirmation = outcome && !running ? lineTestConfirmation(outcome) : null;

  return (
    <Card
      title="Line test"
      className="flex flex-col"
      bodyClassName="flex min-h-0 flex-1 flex-col"
      action={
        <div className="flex items-center justify-end gap-1.5">
          {/* The path chooser, right before Run (operator, 2026-09-13). Same
              height as the small Button; one option alone reads as a label
              and is left disabled. Off entirely while the plan is refused —
              Run then posts no path, as it always did. An undecidable unit
              starts on "Not known" (value ""): picking a path from there is
              the operator's claim; going back to it withdraws the claim. */}
          {plan.isLoading ? (
            <select className="input h-8 w-auto py-1 text-xs" disabled aria-label="Path to measure" title="Reading what this ONU can measure over…">
              <option>…</option>
            </select>
          ) : plan.data && choices.length > 0 ? (
            <select
              className="input h-8 w-auto py-1 text-xs"
              aria-label="Path to measure"
              title={lineTestPathChooserTitle(plan.data.plan)}
              value={path ?? ''}
              onChange={(e) => setPicked(e.target.value === '' ? null : (e.target.value as LineTestPathChoice))}
              disabled={running || choices.length <= 1}
            >
              {choices.map((choice) => (
                <option key={choice.value} value={choice.value}>
                  {choice.label}
                </option>
              ))}
            </select>
          ) : null}
          <Button variant="primary" size="sm" icon={<Gauge className="size-4" />} onClick={start} disabled={running || starting}>
            {starting ? 'Starting…' : running ? 'Running…' : 'Run'}
          </Button>
        </div>
      }
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 p-4">
        {starting ? (
          <p className="text-sm text-muted" role="status">
            Asking {serial} to start the test…
          </p>
        ) : null}
        {confirmation ? (
          <div
            role="status"
            className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-sm ${
              confirmation.tone === 'success'
                ? 'border-success/30 bg-success-soft text-success'
                : confirmation.tone === 'warn'
                  ? 'border-warn/30 bg-warn-soft text-warn'
                  : 'border-danger/30 bg-danger-soft text-danger'
            }`}
          >
            {confirmation.tone === 'success' ? (
              <CheckCircle2 className="mt-0.5 size-4 shrink-0" />
            ) : (
              <XCircle className="mt-0.5 size-4 shrink-0" />
            )}
            <div className="min-w-0 flex-1">
              <p className="font-medium">{confirmation.title}</p>
              <p className="mt-0.5 text-xs opacity-90">{confirmation.detail}</p>
            </div>
            <button
              type="button"
              className="shrink-0 rounded p-0.5 opacity-70 hover:opacity-100"
              aria-label="Dismiss"
              onClick={() => setOutcome(null)}
            >
              <X className="size-3.5" />
            </button>
          </div>
        ) : null}
        {running && inFlight ? (
          <p className="text-sm text-muted">
            The ONU is measuring its {inFlight.path === 'unknown' ? 'line' : SPEED_TEST_PATH_LABELS[inFlight.path]} —{' '}
            <span className="font-medium text-ink">
              {inFlight.phase === 'upload' ? 'upload' : inFlight.phase === 'ping' ? 'latency' : 'download'}
            </span>{' '}
            phase. This card updates every two seconds.
          </p>
        ) : null}

        {last ? (
          <div className="grid min-h-0 flex-1 gap-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
            {/* A third for the figures, two thirds for the results (operator,
                2026-09-13). The last result, read top to bottom the way a plan is quoted:
                down first, then up, then the latency — and under them the
                path it was measured over. */}
            {/* Spread down the column so the figures fill the height the results
                column sets, rather than huddling under the header (operator, 2026-09-13). */}
            <div className="flex flex-col justify-between gap-2">
              <Figure icon={<ArrowDownToLine className="size-3.5" style={{ color: DOWN_COLOR }} />} label="Down" value={formatBitRate(last.downBps)} color={DOWN_COLOR} />
              <Figure icon={<ArrowUpFromLine className="size-3.5" style={{ color: UP_COLOR }} />} label="Up" value={formatBitRate(last.upBps)} color={UP_COLOR} />
              <Figure
                icon={<Timer className="size-3.5 text-ink" />}
                label="Latency"
                value={last.latencyMs === null ? '—' : `${last.latencyMs.toFixed(last.latencyMs < 10 ? 1 : 0)} ms`}
                sub={
                  last.jitterMs !== null || (last.lossPct !== null && last.lossPct > 0) ? (
                    <>
                      {last.jitterMs !== null ? `jitter ${last.jitterMs.toFixed(1)} ms` : null}
                      {last.jitterMs !== null && last.lossPct !== null && last.lossPct > 0 ? ' · ' : null}
                      {last.lossPct !== null && last.lossPct > 0 ? <span className="text-warn">loss {last.lossPct}%</span> : null}
                    </>
                  ) : null
                }
              />
            <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1 text-[11px] text-muted">
              {/* No pill for the expected case — a line run over the subscriber
                  WAN is what the card is for (operator, 2026-09-13). A pill is
                  worn only by a figure that is NOT that: a phone's, or a line
                  measured over the management tunnel or a path not known. */}
              {last.kind === 'phone' ? (
                <Pill tone="neutral" title="Measured by a technician\u2019s phone at the wall">
                  phone
                </Pill>
              ) : last.path !== 'subscriber_wan' ? (
                <Pill tone="warn" title={describeSpeedTestPath(last.path)}>
                  {SPEED_TEST_PATH_LABELS[last.path]}
                </Pill>
              ) : null}
              {last.kind === 'line' && last.path === 'unknown' ? (
                // The one admission this card must make in full: the figure is real, the path is not claimed.
                <span className="text-warn">{describeSpeedTestPath('unknown')}.</span>
              ) : null}
              <span>{formatRelative(last.finishedAt ?? last.requestedAt)}</span>
              {last.status !== 'done' && last.error ? <span className="text-danger">{last.error}</span> : null}
              {/* A done run's note (a rate taken over the interface counter, a
                  ping that did not report) is a footnote, not a warning: two
                  lines at most, the whole sentence on hover — every F670L V7.1
                  carries one now (2026-09-13). */}
              {last.status === 'done' && last.error ? (
                <span className="line-clamp-2 w-full text-right text-[11px] leading-tight text-faint" title={last.error}>
                  {last.error}
                </span>
              ) : null}
            </div>
            </div>

            {/* The past results. The list takes the height the row leaves it
                (contain: size — it never asks for more) and scrolls; on a
                phone, where the two columns stack, it is a fixed 14rem. No
                sparkline over it — the operator asked for the rows alone. */}
            {items.length > 1 && (
              <div className="flex min-h-0 flex-col gap-1.5 sm:border-l sm:border-line sm:pl-4">
                <ul className="h-56 min-h-0 divide-y divide-line overflow-y-auto pr-1 text-xs sm:h-auto sm:flex-1 sm:[contain:size]">
                  {items.map((run) => (
                    <HistoryRow key={run.id} run={run} />
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : history.isLoading ? (
          <p className="text-sm text-muted">Reading the last results…</p>
        ) : (
          <p className="text-sm text-muted">
            No speed test on this ONU yet. Run one and the CPE measures its own line — download, upload, then
            latency to this platform — over TR-069. The result says which path it measured.
          </p>
        )}

      </div>
    </Card>
  );
}

/**
 * One past result: when, over what, and the three figures — a dash where
 * nothing was measured. Built for the column beside three stacked figures,
 * which is about 260 px at the xl breakpoint: the icon carries the whole
 * sentence (path, phone, failure) as its tooltip, the path shows as a word
 * only from 2xl up, and a run that failed says "failed" where its download
 * figure would be rather than three dashes that read as "not measured".
 */
function HistoryRow({ run }: { run: SpeedTest }) {
  const failed = settledStatus(run.status) && run.status !== 'done';
  const path = run.kind === 'phone' ? 'phone' : SPEED_TEST_PATH_LABELS[run.path];
  const title = [
    run.kind === 'phone' ? 'Measured by a technician\u2019s phone at the wall' : describeSpeedTestPath(run.path),
    failed ? `${run.status.replace('_', ' ')}${run.error ? `: ${run.error}` : ''}` : null,
  ]
    .filter(Boolean)
    .join(' — ');
  return (
    <li className="flex items-center gap-1.5 py-1.5" title={title}>
      {run.kind === 'phone' ? (
        <Smartphone className={`size-3.5 shrink-0 ${failed ? 'text-danger' : 'text-muted'}`} />
      ) : (
        <Gauge className={`size-3.5 shrink-0 ${failed ? 'text-danger' : 'text-muted'}`} />
      )}
      <span className="w-12 shrink-0 whitespace-nowrap text-muted">{formatRelative(run.finishedAt ?? run.requestedAt)}</span>
      <span className="hidden min-w-0 flex-1 truncate text-muted 2xl:inline">{path}</span>
      <span className="min-w-0 flex-1 2xl:hidden" />
      {failed ? (
        <span className="w-16 shrink-0 whitespace-nowrap text-right text-danger">failed</span>
      ) : (
        <span className="w-16 shrink-0 whitespace-nowrap text-right tabular-nums" style={{ color: DOWN_COLOR }}>
          {formatBitRate(run.downBps)}
        </span>
      )}
      <span className="w-16 shrink-0 whitespace-nowrap text-right tabular-nums" style={{ color: UP_COLOR }}>
        {formatBitRate(run.upBps)}
      </span>
      <span className="w-9 shrink-0 whitespace-nowrap text-right tabular-nums text-muted">
        {run.latencyMs === null ? '—' : `${Math.round(run.latencyMs)} ms`}
      </span>
    </li>
  );
}

/**
 * One figure of the last result. Sized to stack three of them beside the
 * traffic graph without outgrowing it: the caption a size under the
 * card's labels, the value a size under a stat card's, nothing between.
 * `sub` is the small line under the value (the latency's jitter and loss).
 */
function Figure({
  icon,
  label,
  value,
  color,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color?: string;
  sub?: React.ReactNode;
}) {
  return (
    <div>
      {/* One row, the label to the LEFT of its value (operator, 2026-09-13),
          not over it: three of these read as a small table. */}
      <div className="flex items-baseline justify-between gap-3">
        <div className="flex shrink-0 items-center gap-1.5 text-[11px] font-medium uppercase leading-none tracking-wide text-muted">
          {icon}
          {label}
        </div>
        <div
          className="min-w-0 whitespace-nowrap text-right text-lg font-semibold leading-tight tabular-nums"
          style={color ? { color } : undefined}
        >
          {value}
        </div>
      </div>
      {sub ? <div className="text-right text-[11px] leading-tight text-muted">{sub}</div> : null}
    </div>
  );
}

